import React, { Suspense, useEffect, useReducer, useRef, useState, useSyncExternalStore } from 'react';
import { connect } from 'react-redux';
import { openMatchRoom } from 'react/redux/actions/matchRoom';
import { isNetplayPageActive } from 'react/netplay/netplayPageState';
import MatchDockBar from 'react/dock/MatchDockBar';
import HeaderPlayStatus from 'react/dock/HeaderPlayStatus';
import { opponentsOf, teamNameOf } from 'react/match/rankedPanel/matchTeams';
import useMatchChannel from 'react/dock/useMatchChannel';
import useHostCodeAlert from 'react/dock/useHostCodeAlert';
import { isPhoneWidth } from 'react/dock/phoneWidth';
import DockChat, { DockChatButton } from 'react/dock/chat/DockChat';
import { closeDockChat } from 'react/redux/actions/dockChat';
import {
	forgetCurrentMatch,
	isHomeMatchColumnShowing,
	isHomeMatchColumnShowingOnServer,
	openCurrentMatch,
	reportCurrentMatch,
	setMatchSheetOpener,
	subscribeToMatchDockPlacement,
} from 'react/dock/matchDockPlacement';

const MatchSheet = React.lazy(() => import(/* webpackChunkName: "match-sheet" */ 'react/dock/MatchSheet'));

// The match dock: the bar for the current match on every page, the netplay
// page included, and the sheet above it with the step panel and the match
// chat. Mounted outside #page_content (react/dock/mountMatchDock) so client
// navigation keeps it. Holds the socket's match claim while there is a match,
// fills the header Play link's status and announces the opponent's new host
// code (react/dock/useHostCodeAlert) on every page.
//
// The sheet opens only when the player asks (the bar, its caret or a grid
// step's action), never by itself. On the netplay page the panel is already
// on the page, so there is no sheet and a grid step's action scrolls to the
// panel. Phones get no sheet: the action follows its link to the match.
//
// The home page at 1440px and wider shows the match in a column of its own
// (react/home/HomeMatchColumn). While it shows, the bar stays, as on the
// netplay page, but there is no sheet: the bar's actions go to the column.
// The dock hands its sheet to react/dock/matchDockPlacement, so the home
// page's "Return to your match" can open it.
//
// The dock's chat (react/dock/chat/DockChat) is mounted here too, for every
// logged-in player. During a match its control sits in the bar's corner; the
// chat sheet and the match sheet are never open together.

const NETPLAY_PATH = '/netplay';

function matchHref() {
	return typeof siteUrl === 'string' ? `${siteUrl}${NETPLAY_PATH}` : NETPLAY_PATH;
}

function scrollToNetplayPanel() {
	setTimeout(() => {
		const panel = document.querySelector('.ranked-panel, .match-room');
		if(panel) {
			panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}
	}, 0);
}

function useSheet(inMatch, onNetplay, homeColumnShowing) {
	const [open, setOpen] = useState(false);
	const [, rerender] = useReducer((count) => count + 1, 0);

	useEffect(() => {
		if(!inMatch) {
			setOpen(false);
		}
	}, [inMatch]);

	// Client navigation onto /netplay puts the panel on the page.
	useEffect(() => {
		const navigated = () => {
			if(isNetplayPageActive()) {
				setOpen(false);
			}
			rerender();
		};
		window.addEventListener('smashladder:navigated', navigated);
		return () => window.removeEventListener('smashladder:navigated', navigated);
	}, []);

	// The column takes over from the sheet; it doesn't come back by itself
	// when the column goes (the window narrows, the page changes).
	useEffect(() => {
		if(homeColumnShowing) {
			setOpen(false);
		}
	}, [homeColumnShowing]);

	useEffect(() => {
		if(!open) {
			return undefined;
		}
		const closeOnEscape = (event) => {
			if(event.key === 'Escape') {
				setOpen(false);
			}
		};
		document.addEventListener('keydown', closeOnEscape);
		return () => document.removeEventListener('keydown', closeOnEscape);
	}, [open]);

	const canOpen = !onNetplay && !homeColumnShowing;
	return {
		isOpen: open && canOpen,
		canOpen,
		open: () => {
			if(!canOpen || isPhoneWidth()) {
				return false;
			}
			setOpen(true);
			return true;
		},
		close: () => setOpen(false),
	};
}

// Lets the rest of the page open the sheet (react/dock/matchDockPlacement)
// while there is a match to show.
function useSheetOpener(sheet, showBar) {
	const openRef = useRef(sheet.open);
	openRef.current = sheet.open;
	useEffect(() => {
		if(!showBar) {
			return undefined;
		}
		return setMatchSheetOpener(() => openRef.current());
	}, [showBar]);
}

// Opening either sheet closes the other.
function useOneSheetAtATime(sheet, dockChatOpen, dispatch) {
	const closeMatchSheet = sheet.close;
	useEffect(() => {
		if(dockChatOpen) {
			closeMatchSheet();
		}
	}, [dockChatOpen]);
	useEffect(() => {
		if(sheet.isOpen) {
			dispatch(closeDockChat());
		}
	}, [sheet.isOpen, dispatch]);
}

// Tells the page (the home page's play bar) which match the bar shows: none
// once it is closed or gone.
function useCurrentMatchReport(match) {
	const matchId = match ? match.id : null;
	const opponentName = match ? teamNameOf(opponentsOf(match)) : null;
	useEffect(() => {
		reportCurrentMatch(matchId === null ? null : { match_id: matchId, opponent_name: opponentName });
	}, [matchId, opponentName]);
	useEffect(() => forgetCurrentMatch, []);
}

function MatchDockComponent({ currentMatch, currentMatchUpdateIndex, closingMatchId, dockChatOpen, dispatch }) {
	const inMatch = !!currentMatch;
	useMatchChannel(inMatch);
	useHostCodeAlert(currentMatch, currentMatchUpdateIndex);
	const onNetplay = isNetplayPageActive();
	const homeColumnShowing = useSyncExternalStore(subscribeToMatchDockPlacement, isHomeMatchColumnShowing, isHomeMatchColumnShowingOnServer);
	const sheet = useSheet(inMatch, onNetplay, homeColumnShowing);
	const showBar = inMatch && closingMatchId !== currentMatch.id;
	useSheetOpener(sheet, showBar);
	useOneSheetAtATime(sheet, dockChatOpen, dispatch);
	useCurrentMatchReport(showBar ? currentMatch : null);

	// A grid step's action ("Ban a stage ▲").
	const openPanel = (event) => {
		if(homeColumnShowing) {
			event.preventDefault();
			openCurrentMatch();
			return;
		}
		if(onNetplay) {
			event.preventDefault();
			dispatch(openMatchRoom());
			scrollToNetplayPanel();
			return;
		}
		if(isPhoneWidth()) {
			dispatch(openMatchRoom());
			return;
		}
		event.preventDefault();
		sheet.open();
	};

	return (
		<React.Fragment>
			<HeaderPlayStatus />
			<DockChat barShowing={showBar} />
			{showBar && sheet.isOpen && (
				<Suspense fallback={null}>
					<MatchSheet match={currentMatch} />
				</Suspense>
			)}
			{showBar && (
				<MatchDockBar
					match={currentMatch}
					matchHref={matchHref()}
					onOpenPanel={openPanel}
					sheet={sheet}
					chatControl={onNetplay ? null : <DockChatButton className='match-dock-bar__chat' />}
				/>
			)}
		</React.Fragment>
	);
}

export default connect((state) => ({
	currentMatch: state.matchmaking?.currentMatch || null,
	// The match object is updated in place; the index changes on every update.
	currentMatchUpdateIndex: state.matchmaking?.currentMatchUpdateIndex,
	closingMatchId: state.matchRoom?.closingMatchId ?? null,
	dockChatOpen: !!state.dockChat?.open,
}))(MatchDockComponent);
